import { Navigate, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, Loader2, Menu, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Avatar } from "@/components/avatar";
import { BRDPreview, canPreviewBRD } from "@/components/brd-preview";
import { DeleteRequestButton } from "@/components/delete-request-button";
import { DevelopmentPanel } from "@/components/development-panel";
import { PMReviewControls } from "@/components/pm-review-controls";
import { ProjectObjectiveChip } from "@/components/project-objective-chip";
import { ResponseBanner } from "@/components/request-detail-content";
import { ReviewConsole } from "@/components/review-console";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { downloadBRD, listFeatureRequests } from "@/lib/api/feature-requests";
import { buttonVariants } from "@/lib/button-variants";
import { cn } from "@/lib/cn";
import { relativeDay } from "@/lib/delivery";
import { formatDate, formatDateTime, formatFileSize, formatRelative } from "@/lib/format";
import { daysPastDue, responseDue, responseState } from "@/lib/response-cycle";
import { displayName, type FeatureRequest } from "@/types";

export const Route = createFileRoute("/_app/requests/review")({
  component: PMReviewPage,
});

type QueueFilter =
  | "awaiting"
  | "approved"
  | "deferred"
  | "needs_clarification"
  | "rejected"
  | "all";

const FILTERS: { value: QueueFilter; label: string }[] = [
  { value: "awaiting", label: "Awaiting review" },
  { value: "needs_clarification", label: "Clarify" },
  { value: "approved", label: "Proceed" },
  { value: "deferred", label: "Defer" },
  { value: "rejected", label: "Decline" },
  { value: "all", label: "All requests" },
];

function matchesFilter(request: FeatureRequest, filter: QueueFilter): boolean {
  if (filter === "all") return true;
  if (filter === "awaiting") return request.status === "submitted" || request.status === "under_review";
  return request.status === filter;
}

function PMReviewPage() {
  const { user } = useAuth();
  const isProduct = user?.role === "product" || user?.role === "admin";
  const [filter, setFilter] = useState<QueueFilter>("awaiting");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [summaryFilter, setSummaryFilter] = useState<"late" | "today" | "upcoming" | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const listQuery = useQuery({
    queryKey: ["feature-requests", "all"],
    queryFn: () => listFeatureRequests(),
    enabled: isProduct,
  });
  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const awaiting = useMemo(
    () => rows.filter((r) => r.status === "submitted" || r.status === "under_review"),
    [rows],
  );
  const summary = useMemo(() => {
    const states = awaiting.map((r) => responseState(r).kind);
    return {
      late: states.filter((s) => s === "overdue").length,
      today: states.filter((s) => s === "due_today").length,
      upcoming: states.filter((s) => s === "awaiting").length,
    };
  }, [awaiting]);

  const queue = useMemo(() => {
    let filtered = rows.filter((r) => matchesFilter(r, filter));
    if (summaryFilter) {
      filtered = filtered.filter((r) => {
        const kind = responseState(r).kind;
        if (summaryFilter === "late") return kind === "overdue";
        if (summaryFilter === "today") return kind === "due_today";
        return kind === "awaiting";
      });
    }
    return filtered.sort((a, b) => {
      if (filter === "awaiting") {
        return responseDue(a).localeCompare(responseDue(b)) || a.created_at.localeCompare(b.created_at);
      }
      return b.created_at.localeCompare(a.created_at);
    });
  }, [rows, filter, summaryFilter]);

  useEffect(() => {
    if (queue.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !queue.some((r) => r.id === selectedId)) {
      setSelectedId(queue[0].id);
    }
  }, [queue, selectedId]);

  useEffect(() => {
    if (!queueOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setQueueOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [queueOpen]);

  if (user && !isProduct) return <Navigate to="/" />;

  const selectedIndex = queue.findIndex((r) => r.id === selectedId);
  const selected = selectedIndex >= 0 ? queue[selectedIndex] : null;

  const summaryItems = [
    { key: "late" as const, label: "Late", value: summary.late },
    { key: "today" as const, label: "Due today", value: summary.today },
    { key: "upcoming" as const, label: "Upcoming", value: summary.upcoming },
  ];

  const selectId = (id: number) => {
    setSelectedId(id);
    setQueueOpen(false);
  };

  const jumpLabel = (request: FeatureRequest, index: number) => {
    const state = responseState(request);
    const suffix =
      state.kind === "overdue"
        ? ` · Late ${daysPastDue(request)} days`
        : state.kind === "due_today"
          ? " · Due today"
          : "";
    return `${index + 1} of ${queue.length} · ${request.title}${suffix}`;
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-col gap-4 min-[1100px]:h-[calc(100svh-8.75rem)]">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-xs font-medium tracking-widest text-primary uppercase">Product managers</p>
          <h1 className="font-heading text-xl leading-tight font-semibold tracking-tight">Review submissions</h1>
          <p className="mt-1 max-w-[62ch] text-[13px] text-muted-foreground">
            Submitted Mon–Tue: decide by Fri the same week. Submitted Wed–Sun: decide by the next Wed.
          </p>
        </div>

        <div className="flex flex-wrap overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10" role="group" aria-label="Review deadlines">
          {summaryItems.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={summaryFilter === item.key}
              onClick={() => {
                setSummaryFilter((current) => (current === item.key ? null : item.key));
                setFilter("awaiting");
              }}
              className={cn(
                "grid min-w-20 border-r border-border px-3.5 py-2 text-left last:border-r-0",
                summaryFilter === item.key && "bg-primary/10 text-primary shadow-[inset_0_-3px_0_var(--primary)]",
              )}
            >
              <span
                className={cn(
                  "font-heading text-[22px] leading-none font-semibold tracking-tight tabular-nums",
                  item.key === "late" && summaryFilter !== item.key && "text-destructive",
                )}
              >
                {item.value}
              </span>
              <span className="mt-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-x-3.5 gap-y-2 rounded-t-xl border border-b-0 border-border bg-muted/40 px-3 py-2.5">
        <label className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
          <span>Show</span>
          <Select
            value={filter}
            onValueChange={(value) => {
              setFilter(value as QueueFilter);
              setSummaryFilter(null);
            }}
          >
            <SelectTrigger aria-label="Show" className="h-8 w-auto min-w-[10rem] bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={selectedIndex <= 0}
            aria-label="Previous request"
            onClick={() => selectId(queue[selectedIndex - 1].id)}
          >
            <ChevronLeft className="size-3.5" /> Prev
          </Button>
          <Select
            value={selected ? String(selected.id) : undefined}
            onValueChange={(value) => selectId(Number(value))}
            disabled={queue.length === 0}
          >
            <SelectTrigger aria-label="Jump to request" className="h-8 min-w-0 flex-1 bg-background font-semibold sm:max-w-[36rem]">
              <SelectValue placeholder="Pick a request" />
            </SelectTrigger>
            <SelectContent>
              {queue.map((request, index) => (
                <SelectItem key={request.id} value={String(request.id)}>
                  {jumpLabel(request, index)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={selectedIndex < 0 || selectedIndex >= queue.length - 1}
            aria-label="Next request"
            onClick={() => selectId(queue[selectedIndex + 1].id)}
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>

        <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={() => setQueueOpen(true)}>
          <Menu className="size-3.5" /> Queue ({queue.length})
        </Button>
      </div>

      <div className="grid min-h-[560px] min-w-0 flex-1 overflow-hidden rounded-b-xl border border-border bg-card shadow-xs min-[1100px]:min-h-0 min-[1100px]:grid-cols-[minmax(0,1.5fr)_minmax(380px,1fr)]">
        {selected ? (
          <>
            <section
              className="flex min-h-0 min-w-0 flex-col border-b border-border min-[1100px]:border-r min-[1100px]:border-b-0"
              aria-label="BRD document"
            >
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-3.5 py-2.5">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold">BRD document</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {selected.brd
                      ? `${selected.brd.file_name} · ${formatFileSize(selected.brd.file_size)}`
                      : "No document attached"}
                  </p>
                </div>
                {selected.brd ? (
                  <button
                    type="button"
                    disabled={downloading}
                    className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                    onClick={async () => {
                      setDownloading(true);
                      try {
                        await downloadBRD(selected.id, selected.brd?.file_name ?? "brd-document");
                      } finally {
                        setDownloading(false);
                      }
                    }}
                  >
                    {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
                    Download
                  </button>
                ) : null}
              </div>
              <div className="relative min-h-0 flex-1 overflow-hidden bg-muted/20">
                {selected.brd && canPreviewBRD(selected.brd.file_name) ? (
                  <BRDPreview
                    requestId={selected.id}
                    fileName={selected.brd.file_name}
                    className="absolute inset-0 h-full w-full"
                  />
                ) : (
                  <div className="grid place-items-start gap-1 overflow-auto p-6">
                    <p className="font-semibold">
                      {selected.brd ? "Preview unavailable" : "No document attached"}
                    </p>
                    <p className="max-w-sm text-[13px] text-muted-foreground">
                      {selected.brd
                        ? "Download the file to open it locally."
                        : "Ask the requester for their BRD, or choose Clarify."}
                    </p>
                  </div>
                )}
              </div>
            </section>

            <aside className="min-h-0 min-w-0 overflow-y-auto" aria-label="Request detail and PM actions">
              <div className="grid gap-5 px-5 py-5">
                <ReviewDetailHeader request={selected} onDeleted={() => setSelectedId(null)} />

                <dl className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <dt className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                      Expected go-live
                    </dt>
                    <dd className="text-sm">
                      <span className="font-mono tabular-nums">
                        {selected.expected_deadline ? formatDate(selected.expected_deadline) : "—"}
                      </span>
                      {selected.expected_deadline && relativeDay(selected.expected_deadline) ? (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {relativeDay(selected.expected_deadline)}
                        </span>
                      ) : null}
                    </dd>
                  </div>
                  <div className="grid gap-1">
                    <dt className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                      BRD file
                    </dt>
                    <dd className="text-sm break-all">
                      {selected.brd?.file_name ?? "Not attached"}
                    </dd>
                  </div>
                  {selected.description?.trim() ? (
                    <div className="grid gap-1 sm:col-span-2">
                      <dt className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                        Requester notes
                      </dt>
                      <dd className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm whitespace-pre-wrap">
                        {selected.description.trim()}
                      </dd>
                    </div>
                  ) : null}
                  {selected.ceo_highlight && selected.ceo_note?.trim() ? (
                    <div className="grid gap-1 sm:col-span-2">
                      <dt className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                        Note for the CEO
                      </dt>
                      <dd className="text-sm whitespace-pre-wrap">{selected.ceo_note.trim()}</dd>
                    </div>
                  ) : null}
                </dl>

                <div className="overflow-hidden rounded-xl border border-border border-t-4 border-t-primary bg-card">
                  <div className="grid gap-0.5 border-b border-border bg-muted/40 px-4 py-3">
                    <strong className="font-heading text-[15px] font-semibold tracking-wide uppercase">
                      PM actions
                    </strong>
                    <span className="text-xs text-muted-foreground">
                      Flags, owner and timeline save as you change them.
                    </span>
                  </div>
                  <div className="grid gap-5 px-4 py-4">
                    <ReviewConsole key={`${selected.id}-${selected.status}`} request={selected} layout="panel" />
                    <PMReviewControls key={`pm-${selected.id}`} request={selected} />
                  </div>
                </div>

                {selected.status === "approved" && selected.jira_issue_key?.trim() ? (
                  <DevelopmentPanel requestId={selected.id} />
                ) : null}

                <DecisionLog request={selected} />
              </div>
            </aside>
          </>
        ) : (
          <div className="col-span-full grid place-items-center px-6 py-16 text-center">
            <div>
              <p className="font-heading text-lg font-semibold tracking-tight">Nothing to review here</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                {listQuery.isLoading
                  ? "Loading the review queue…"
                  : summaryFilter
                    ? "No requests in this deadline group."
                    : "Change the filter above, or open the queue."}
              </p>
            </div>
          </div>
        )}
      </div>

      {queueOpen ? (
        <div className="fixed inset-0 z-40" role="dialog" aria-label="Review queue">
          <button
            type="button"
            className="absolute inset-0 bg-black/35"
            aria-label="Close queue"
            onClick={() => setQueueOpen(false)}
          />
          <aside className="absolute inset-y-0 right-0 flex w-full max-w-[420px] flex-col bg-card shadow-[-10px_0_30px_rgba(0,0,0,.15)]">
            <div className="flex items-center justify-between border-b border-border px-4 py-3.5">
              <strong className="font-heading text-sm font-semibold tracking-wide uppercase">Review queue</strong>
              <Button type="button" variant="outline" size="sm" onClick={() => setQueueOpen(false)}>
                <X className="size-3.5" /> Close
              </Button>
            </div>
            {listQuery.isLoading ? (
              <div className="grid gap-0" aria-busy="true" aria-label="Loading review queue">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="animate-pulse border-b border-border px-3.5 py-3">
                    <div className="h-3.5 w-3/4 rounded-md bg-muted" />
                    <div className="mt-2 h-3 w-1/2 rounded-md bg-muted" />
                  </div>
                ))}
              </div>
            ) : queue.length === 0 ? (
              <p className="px-3.5 py-5 text-[13px] leading-relaxed text-muted-foreground">
                {summaryFilter
                  ? "No requests in this group."
                  : filter === "awaiting"
                    ? "Nothing awaiting review. New submissions appear here."
                    : "No requests with this status."}
              </p>
            ) : (
              <ul className="min-h-0 flex-1 overflow-y-auto">
                {queue.map((request) => (
                  <li key={request.id}>
                    <button
                      type="button"
                      aria-current={request.id === selectedId}
                      onClick={() => selectId(request.id)}
                      className={cn(
                        "grid w-full gap-1 border-b border-border px-3.5 py-3 text-left transition-colors",
                        request.id === selectedId
                          ? "bg-muted shadow-[inset_4px_0_0_var(--primary)]"
                          : "bg-card hover:bg-muted/50",
                      )}
                    >
                      <span className="text-sm leading-snug font-semibold">{request.title}</span>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span className="font-mono">LQ-{request.id}</span>
                        {request.directorate ? <span>{request.directorate}</span> : null}
                        <ProjectObjectiveChip objective={request.project_objective} className="h-[18px] px-1.5 py-0 text-[10px]" />
                        {request.ceo_highlight ? (
                          <span className="rounded-full bg-primary px-1.5 py-px text-[10px] font-medium text-primary-foreground">
                            CEO
                          </span>
                        ) : null}
                        {request.adhoc ? (
                          <span className="rounded-sm border border-destructive px-1.5 py-px text-[10px] font-semibold tracking-wide text-destructive uppercase">
                            Ad-hoc
                          </span>
                        ) : null}
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={request.status} />
                        <DeadlineTag request={request} />
                        <span className="text-[11px] text-muted-foreground">
                          {formatRelative(request.created_at)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function ReviewDetailHeader({
  request,
  onDeleted,
}: {
  request: FeatureRequest;
  onDeleted?: () => void;
}) {
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">LQ-{request.id}</span>
        <StatusBadge status={request.status} />
        <ProjectObjectiveChip objective={request.project_objective} className="h-[22px] px-2 py-0" />
        {request.ceo_highlight ? (
          <span className="inline-flex h-[22px] items-center rounded-full bg-primary px-2 text-[11px] font-medium text-primary-foreground">
            CEO HIGHLIGHT
          </span>
        ) : null}
        {request.adhoc ? (
          <span className="inline-flex h-[22px] items-center rounded-sm border border-destructive px-2 text-[11px] font-semibold tracking-wide text-destructive uppercase">
            Ad-hoc
          </span>
        ) : null}
        <span className="ml-auto">
          <DeleteRequestButton requestId={request.id} title={request.title} onDeleted={onDeleted} />
        </span>
      </div>

      <h2 className="font-heading text-2xl leading-tight font-semibold tracking-tight text-balance">
        {request.title}
      </h2>

      <div className="flex flex-wrap items-center gap-3 text-[13px]">
        <Avatar name={displayName(request.requester, request.requester_id)} />
        <div className="min-w-0">
          <p>
            <span className="text-muted-foreground">Requested by </span>
            <strong>{displayName(request.requester, request.requester_id)}</strong>
            <span className="text-muted-foreground"> · {request.directorate?.trim() || "—"}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Submitted {formatDateTime(request.created_at)}
            {request.created_at ? ` · ${formatRelative(request.created_at)}` : null}
          </p>
        </div>
      </div>

      <ResponseBanner request={request} />
    </div>
  );
}

function DecisionLog({ request }: { request: FeatureRequest }) {
  const events = [...(request.events ?? [])].reverse();
  if (events.length === 0) {
    return (
      <section className="grid gap-2">
        <h3 className="text-[11.5px] font-semibold tracking-widest text-primary uppercase">Decision log</h3>
        <p className="text-[13px] text-muted-foreground">No decision yet.</p>
      </section>
    );
  }
  return (
    <section className="grid gap-2">
      <h3 className="text-[11.5px] font-semibold tracking-widest text-primary uppercase">Decision log</h3>
      <ol className="grid gap-0 divide-y divide-border border border-border">
        {events.map((event) => (
          <li key={event.id} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
            <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
              {formatDateTime(event.created_at)}
            </span>
            <div className="min-w-0">
              <p className="text-[13px] font-medium">
                {event.from_status ? `${event.from_status} → ` : ""}
                {event.to_status}
                {event.actor ? (
                  <span className="font-normal text-muted-foreground">
                    {" "}
                    · {displayName(event.actor, event.actor_id)}
                  </span>
                ) : null}
              </p>
              {event.note?.trim() ? (
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap">
                  {event.note.trim()}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function DeadlineTag({ request }: { request: FeatureRequest }) {
  const state = responseState(request);
  if (state.kind === "responded") {
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-full border border-success/30 bg-success-background px-1.5 py-0.5 text-[11px] font-medium",
          state.late ? "border-dashed border-destructive/40 bg-destructive/10 text-destructive" : "text-success",
        )}
      >
        {state.late ? "Answered late" : "Answered on time"}
      </span>
    );
  }
  if (state.kind === "overdue") {
    return (
      <span className="inline-flex items-center rounded-full bg-destructive px-1.5 py-0.5 text-[11px] font-medium text-destructive-foreground">
        Late
      </span>
    );
  }
  if (state.kind === "due_today") {
    return (
      <span className="inline-flex items-center rounded-full bg-warning px-1.5 py-0.5 text-[11px] font-medium text-white">
        Due today
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
      Due {formatDate(state.due)}
    </span>
  );
}
